import { RefObject, useEffect, useRef, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { flexRender, Row, Table } from '@tanstack/react-table'
import clsx from 'clsx'

import { Log, LogSource } from '@/types/logs'
import { TableFormEdit } from './TableForm/TableFormEdit'
import { InputFormValues } from '../../types'
import { useCheckShouldHighlightRowAsUrgent } from '../../helpers/useCheckShouldHighlightRowAsUrgent'
import { TableCell, TableRow } from '@/ui/Table'

import styles from '../CombinedLogs.module.scss'

interface Props {
    row: Row<Log>
    rowIndex: number
    array: Row<Log>[]
    editWrapperRef: RefObject<HTMLTableRowElement>
    sharedLogRef?: RefObject<HTMLTableRowElement>
    form: UseFormReturn<InputFormValues, void, InputFormValues>
    table: Table<Log>
    multipleSelection: Log[]
    setIsCreateFormActive?: (value: boolean) => void
    onShiftClick: (event: React.MouseEvent<HTMLTableRowElement>, row: Row<Log>) => void
    onClick: (row: Row<Log>) => void
}

export const CombinedLogRow = ({
    row,
    editWrapperRef,
    sharedLogRef,
    form,
    table,
    multipleSelection,
    rowIndex,
    array,
    setIsCreateFormActive,
    onShiftClick,
    onClick,
}: Props) => {
    const [searchParams] = useSearchParams()
    const { checkShouldHighlightRowAsUrgent } = useCheckShouldHighlightRowAsUrgent()

    const isUrgent = checkShouldHighlightRowAsUrgent(row.original)
    const isLocked = row.original.isLocked
    const ref = useRef<HTMLTableRowElement>(null)
    const [isPopupOpen, setIsPopupOpen] = useState(false)

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const popupOpen = document.querySelector('[data-popup-open="true"]')

            if (popupOpen) {
                setIsPopupOpen(true)
            } else {
                setIsPopupOpen(false)
            }
        })

        if (sharedLogRef?.current && searchParams.get('sharedLogId') === row.original.id) {
            observer.observe(sharedLogRef?.current, { childList: true, subtree: true })
        }

        if (ref?.current) {
            observer.observe(ref.current, { childList: true, subtree: true })
        }

        return () => observer.disconnect()
    }, [row.original.id, searchParams, sharedLogRef])
    const checkIsLinkedLog = (currentLog: Log, rowIndex: number, logs: Row<Log>[]) => {
        return (
            currentLog.eventLog &&
            logs.some((log) => log.id === currentLog.eventLog?.id) &&
            !!rowIndex &&
            (logs.at(rowIndex - 1)?.original.eventLog?.id === currentLog.eventLog?.id ||
                logs.at(rowIndex - 1)?.original?.id === currentLog.eventLog?.id)
        )
    }

    if (
        row.getIsSelected() &&
        row.original.source === LogSource.Manual &&
        row.original.codingPeriodInfo.permissions.includes('Update') &&
        !row.original.isLocked
    ) {
        return (
            <TableFormEdit
                ref={editWrapperRef}
                form={form}
                isActive={true}
                table={table}
                row={row}
                isUrgent={isUrgent}
            />
        )
    }

    return (
        <TableRow
            row={row}
            key={row.id}
            ref={searchParams.get('sharedLogId') === row.original.id ? sharedLogRef : ref}
            setIsCreateFormActive={setIsCreateFormActive}
            isUrgent={isUrgent}
            isLocked={isLocked}
            className={clsx({
                [styles.selectedRow]:
                    row?.getIsSelected() ||
                    multipleSelection.some((log) => log.id === row.original.id) ||
                    searchParams.get('sharedLogId') === row.original.id,
                [styles.urgentRow]: isUrgent,
                [styles.lockedRow]: isLocked,
                [styles.isOpenPopup]: isPopupOpen,
            })}
            onClick={() => onClick(row)}
            onShiftClick={(event, rowData) => onShiftClick(event, rowData as Row<Log>)}
        >
            {row.getVisibleCells().map((cell, cellIndex) => (
                <TableCell
                    key={cell.id}
                    alignCenter={cell.column.columnDef.meta?.alignCenter}
                    className={clsx({
                        [styles.linkElement]: !cellIndex && checkIsLinkedLog(row.original, rowIndex, array),
                        [styles.codeCell]: !cellIndex,
                        [styles.centeredCell]:
                            !row.original.originalTrip && !row.getIsSelected() && !row.original.bulkCurtailment,
                    })}
                >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
            ))}
        </TableRow>
    )
}